import React from 'react';

type FallbackRender = (props: { error: Error; reset: () => void }) => React.ReactNode;

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback: React.ReactNode | FallbackRender;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: ReadonlyArray<unknown>;
};

type ErrorBoundaryState = {
  error: Error | null;
};

const initialState: ErrorBoundaryState = { error: null };

const haveResetKeysChanged = (
  prev: ReadonlyArray<unknown> = [],
  next: ReadonlyArray<unknown> = [],
) => prev.length !== next.length || prev.some((value, index) => !Object.is(value, next[index]));

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = initialState;

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error === null) return;

    if (haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = () => {
    this.props.onReset?.();
    this.setState(initialState);
  };

  override render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error === null) return children;

    return typeof fallback === 'function' ? fallback({ error, reset: this.reset }) : fallback;
  }
}
